'use client'

import Link from 'next/link'
import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react'

import { BillApproval } from '@/components/legislative-tracker/BillApproval'
import { CategoryIcons } from '@/components/legislative-tracker/BillCard/components/CategoryIcons'
import { ScopeTab } from '@/components/legislative-tracker/BillCard/components/ScopeTab'
import { TrackTag } from '@/components/legislative-tracker/BillCard/components/TrackTag'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useUserTracking } from '@/providers/UserTracking'
import {
  billStateCodeToScopeSlug,
  getBillIdentifier,
  getBillLegislativeTrackerUrl,
} from '@/utilities/billTrack50/billRouting'
import { getBillCardStatusDisplay } from '@/utilities/billTrack50/getBillProgressTimeline'
import { getStateObjectFromSlug } from '@/utilities/getStateObject'
import { cn } from '@/utilities/ui'

import type { Bill, Topic } from '@/payload-types'

export const BillCard = ({
  ref,
  bill,
  allBillTopics,
  size = 'default',
  tabIndex,
  style,
  trackedBillIds: trackedBillIdsProp,
  onToggleTrack: onToggleTrackProp,
  useBillProgress = false,
}: {
  ref?: React.Ref<HTMLDivElement | HTMLAnchorElement>
  bill: Bill
  allBillTopics: Topic[]
  size?: 'small' | 'default'
  tabIndex?: number
  style?: React.CSSProperties
  /** When provided with onToggleTrack, shows save/unsave tag. Omit when user is not logged in. */
  trackedBillIds?: number[]
  onToggleTrack?: (billId: number) => Promise<void>
  /** Jurisdiction scope pages: show `bt50.billProgress` and `actionDate` instead of scraped timeline. */
  useBillProgress?: boolean
}) => {
  const trackingContext = useUserTracking()
  const trackedBillIds = trackedBillIdsProp ?? trackingContext?.trackedBillIds
  const onToggleTrack = onToggleTrackProp ?? trackingContext?.onToggleTrack

  const isTracked =
    trackedBillIds != null && onToggleTrack != null && trackedBillIds.includes(bill.id)
  const showTrackControl = trackedBillIds != null && onToggleTrack != null

  const trackTag = (
    <TrackTag
      billId={bill.id}
      isTracked={isTracked}
      onToggleTrack={showTrackControl ? onToggleTrack : undefined}
      size={size ?? 'default'}
    />
  )

  const { title, topics, mahaApproved } = bill
  const billIdentifier = getBillIdentifier(bill)
  // const billSummary =
  //   description || bill._mahaDescription || openStatesApiData?._billSummary || null
  const approval =
    mahaApproved === 'approved' || mahaApproved === 'not-approved' ? mahaApproved : 'not-reviewed'

  // Get scope & full state name
  const scopeSlug = billStateCodeToScopeSlug(bill.bt50?.stateCode)
  const stateObject = getStateObjectFromSlug(scopeSlug)
  const scopeName = stateObject?.label || 'Federal'

  const billUrl = getBillLegislativeTrackerUrl(bill) ?? '#'
  const { dateLabel: billStatusDate, statusLabel: billStatusLabel } = getBillCardStatusDisplay(
    bill,
    { useBillProgress },
  )

  if (size === 'small') {
    const cardShellClass = cn(
      'bill-card--small gap-4 overflow-hidden px-5 pb-4',
      'bill-card relative flex flex-col',
      'rounded-2xl bg-white transition-colors duration-200 hover:border-border-hover',
      'border border-border',
      'w-full sm:max-w-[406px]',
    )

    const linkClass = cn(
      '-mx-1 flex min-h-0 min-w-0 flex-1 flex-col gap-2 rounded-xl px-1',
      'outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0',
      'focus:ring-2 focus:ring-evergreen-500 focus:ring-offset-0',
    )

    return (
      <div ref={ref as React.Ref<HTMLDivElement>} className={cardShellClass} style={style}>
        <div className="pointer-events-auto isolate flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <ScopeTab scopeName={scopeName} />
            {trackTag}
          </div>
          <CategoryIcons
            size="small"
            allTopics={allBillTopics}
            specificTopics={topics as Topic[]}
          />
        </div>

        <Link href={billUrl || '#'} className={linkClass} tabIndex={tabIndex}>
          <h3 className="m-0 line-clamp-1 p-0 font-display text-xl font-normal text-ellipsis text-primary">
            Bill: {billIdentifier || title || 'Untitled'}
          </h3>
          {bill.title && (
            <p
              className="m-0 line-clamp-2 h-10 p-0 font-body text-sm text-ellipsis text-card-foreground"
              title={bill.title}
            >
              {bill.title.replace(/_/g, ' ')}
            </p>
          )}
          <div className="flex h-7 items-center justify-between gap-2">
            <p className="m-0 flex items-center gap-2 p-0 font-body text-sm font-medium text-primary opacity-80">
              <span>{billStatusDate}</span>
              <span className="text-muted-foreground">&bull;</span>
              <span>{billStatusLabel}</span>
            </p>
            {approval !== 'not-reviewed' && (
              <span className="flex size-7 items-center justify-center rounded-xl bg-neutral-200 text-neutral-500 transition-transform hover:scale-105">
                {approval === 'approved' ? (
                  <ThumbsUpIcon className="size-3" />
                ) : (
                  <ThumbsDownIcon className="size-3" />
                )}
              </span>
            )}
          </div>
        </Link>
      </div>
    )
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn(
        'bill-card bill-card--default gap-3 px-4 pb-4 md:gap-5 md:px-8 md:pb-6',
        'relative flex flex-col rounded-2xl bg-white',
        'border border-border',
        'w-full max-w-full min-w-full sm:w-[406px] sm:max-w-[406px] sm:min-w-[406px]',
      )}
      style={style}
    >
      {/* Wrapper for state tab and category icons */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {/* Scope tab */}
          <ScopeTab scopeName={scopeName} />
          {trackTag}
        </div>

        {/* Approval status */}
        {approval !== 'not-reviewed' && (
          <Tooltip>
            <TooltipTrigger className="relative right-5 md:right-0">
              <span className="flex size-8 items-center justify-center rounded-b-xl bg-neutral-200 text-neutral-500 transition-transform hover:scale-105">
                {approval === 'approved' ? (
                  <ThumbsUpIcon className="size-4" />
                ) : (
                  <ThumbsDownIcon className="size-4" />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent asChild side="bottom">
              <div className="relative w-fit border-none bg-transparent shadow-none drop-shadow-md">
                <BillApproval mahaApproval={approval} />
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-3">
        {/* Bill title */}
        <h3 className="m-0 p-0 font-display text-4xl leading-[1em] font-normal text-primary">
          Bill: {billIdentifier || title || 'Untitled'}
        </h3>

        {/* Description */}
        {bill.title && (
          <p
            className="m-0 line-clamp-2 h-12 p-0 font-body text-base text-ellipsis text-primary"
            title={bill.title}
          >
            {bill.title.replace(/_/g, ' ')}
          </p>
        )}

        {/* Date and status */}
        <p className="m-0 flex items-center gap-2 p-0 font-body text-base font-normal text-primary opacity-80">
          <span>{billStatusDate}</span>
          <span className="text-muted-foreground">&bull;</span>
          <span>{billStatusLabel}</span>
        </p>
      </div>

      <div className="mt-auto flex items-center justify-between">
        {/* Support button */}
        <Button variant="outline" size="small" className="w-fit" labelClassName="w-fit" asChild>
          <Link href={billUrl || '#'}>
            View bill<span className="sr-only"> {billIdentifier}</span>
          </Link>
        </Button>

        {/* Category icons */}
        <CategoryIcons allTopics={allBillTopics} specificTopics={topics as Topic[]} />
      </div>
    </div>
  )
}
